import { useState } from "react"
import { Alert, Button, Input, Space, Tag, Tooltip, message } from "antd"
import {
  CopyOutlined,
  DesktopOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LinkOutlined,
  PoweroffOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons"
import { Monitor } from "iconsax-react"
import StatusTag from "../StatusTag/StatusTag"
import DevicePanelCard from "../DevicePanelCard/DevicePanelCard"
import {
  copyText,
  hasRustdeskId,
  launchRustdesk,
  osLabel,
  queueRemoteAction,
  sessionStatusOf,
} from "../../Utils/remoteSupport"
import { renderDate } from "../../Utils/utils"
import { useHeaderContext } from "../../Utils/HeaderContext"
import "./RemoteAccessPanel.scss"

const RemoteAccessPanel = ({ device, onRefresh }) => {
  const { setTaskId, setRender, updateDeviceTask, isDeviceTaskActive } =
    useHeaderContext()
  const [busy, setBusy] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const ready = hasRustdeskId(device)
  const session = sessionStatusOf(device)
  const connecting =
    session === "connecting" ||
    isDeviceTaskActive?.(device?.hostname, `connect:${device?.rustdesk_id}`)
  const disconnecting =
    session === "disconnecting" ||
    isDeviceTaskActive?.(device?.hostname, `disconnect:${device?.rustdesk_id}`)
  const connected = session === "connected"
  const sessionStatus = connecting
    ? "connecting"
    : disconnecting
      ? "disconnecting"
      : session

  const run = async (action) => {
    setBusy(action)
    try {
      await queueRemoteAction({
        device,
        action,
        setTaskId,
        setRender,
        updateDeviceTask,
      })
      onRefresh?.()
    } catch (err) {
      message.error(err.message || "Remote action failed")
    } finally {
      setBusy("")
    }
  }

  return (
    <DevicePanelCard
      title="Remote access"
      description="Start RustDesk on the endpoint, then join from this computer."
      icon={<Monitor size={18} variant="Bold" />}
      iconTone={connected ? "ok" : ready ? undefined : "warn"}
      status={
        <>
          <Tag color={ready ? "green" : "gold"}>
            {ready ? "ID reported" : "Not registered"}
          </Tag>
          <StatusTag status={sessionStatus} />
        </>
      }
      extra={
        <div className="ds-card__actions" style={{ marginTop: 0 }}>
          <Button
            type="primary"
            size="small"
            icon={<ThunderboltOutlined />}
            disabled={!ready || device?.deviceStatus === "Offline"}
            loading={busy === "connect" || connecting}
            onClick={() => run("connect")}
          >
            Start
          </Button>
          <Button
            size="small"
            danger
            icon={<PoweroffOutlined />}
            disabled={!ready}
            loading={busy === "disconnect" || disconnecting}
            onClick={() => run("disconnect")}
          >
            End
          </Button>
        </div>
      }
    >
      <div className="remote-access-panel">
        {!ready ? (
          <Alert
            type="warning"
            showIcon
            message="RustDesk is not registered on this device"
            description={
              device?.agentType === "full" ||
              String(device?.agentversion || "").startsWith("full")
                ? "The Device Manager agent is online, but this PC has not reported a RustDesk ID. Install RustDesk, open it once so it creates an ID, set a permanent password in RustDesk Security, then Update agent to 1.30 and wait for the next check-in."
                : "Sync the endpoint so the agent can report its RustDesk ID. You cannot start a session until that ID is present."
            }
          />
        ) : (
          <Alert
            type={connected ? "success" : "info"}
            showIcon
            message={
              connected
                ? "Remote access is active on this device"
                : "Start access on the device, then join from RustDesk on this computer"
            }
            description="Connect queues the agent to start RustDesk on the endpoint. Copy the ID and password, or open the RustDesk app, then disconnect when you are finished."
          />
        )}

        <dl className="ds-kv-grid">
          <div className="ds-kv">
            <dt>Hostname</dt>
            <dd>{device?.hostname || "—"}</dd>
          </div>
          <div className="ds-kv">
            <dt>User</dt>
            <dd>{device?.user || "Unassigned"}</dd>
          </div>
          <div className="ds-kv">
            <dt>OS</dt>
            <dd>{osLabel(device)}</dd>
          </div>
          <div className="ds-kv">
            <dt>Device</dt>
            <dd>
              <StatusTag status={device?.deviceStatus} />
            </dd>
          </div>
          <div className="ds-kv">
            <dt>Session</dt>
            <dd>
              <StatusTag status={sessionStatus} />
            </dd>
          </div>
          <div className="ds-kv">
            <dt>Last connected</dt>
            <dd>{renderDate(device?.lastRemoteConnectAt) || "—"}</dd>
          </div>
          <div className="ds-kv">
            <dt>RustDesk ID</dt>
            <dd>
              <Space>
                <span className="ds-mono">{device?.rustdesk_id || "—"}</span>
                {ready ? (
                  <Tooltip title="Copy ID">
                    <CopyOutlined
                      onClick={() =>
                        copyText(device.rustdesk_id, "RustDesk ID copied")
                      }
                    />
                  </Tooltip>
                ) : null}
              </Space>
            </dd>
          </div>
          <div className="ds-kv">
            <dt>Password</dt>
            <dd>
              {device?.rustdesk_password ? (
                <Space>
                  <Input
                    readOnly
                    size="small"
                    type={showPassword ? "text" : "password"}
                    value={device.rustdesk_password}
                    className="remote-access-panel__secret"
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={
                      showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />
                    }
                    onClick={() => setShowPassword((pre) => !pre)}
                  />
                  <Tooltip title="Copy password">
                    <CopyOutlined
                      onClick={() =>
                        copyText(device.rustdesk_password, "Password copied")
                      }
                    />
                  </Tooltip>
                </Space>
              ) : (
                "Not reported"
              )}
            </dd>
          </div>
        </dl>

        <div className="ds-card__actions">
          <Button
            icon={<LinkOutlined />}
            disabled={!ready}
            onClick={() => launchRustdesk(device)}
          >
            Open in RustDesk
          </Button>
          <Button
            icon={<CopyOutlined />}
            disabled={!ready}
            onClick={() =>
              copyText(
                device.rustdesk_password
                  ? `${device.rustdesk_id} / ${device.rustdesk_password}`
                  : device.rustdesk_id,
                "Credentials copied"
              )
            }
          >
            Copy credentials
          </Button>
        </div>

        {device?.deviceStatus === "Offline" ? (
          <p className="remote-access-panel__hint">
            <DesktopOutlined /> The device is offline. Start remote access after
            it comes back online.
          </p>
        ) : null}
      </div>
    </DevicePanelCard>
  )
}

export default RemoteAccessPanel
